import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { MobileBar } from './MobileBar';
import { TopStrip } from './TopStrip';
import { CommandPalette } from './CommandPalette';

interface AppShellProps {
  children:  React.ReactNode;
  userEmail: string | null;
  onSignOut: () => void;
}

/**
 * Full-height layout. From 1024px: sidebar, system strip, page content.
 * Below it: a top bar with the menu, search and refresh, and the sidebar slides in as a drawer.
 */
export const AppShell: React.FC<AppShellProps> = ({ children, userEmail, onSignOut }) => {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname, search } = useLocation();
  const navType = useNavigationType();
  const mainRef = useRef<HTMLElement>(null);
  const scrollByUrl = useRef(new Map<string, number>());
  const lastPath = useRef(pathname);
  const currentUrl = useRef(pathname + search);

  // The page scrolls inside <main>, so the browser never resets it. A new page starts at the top;
  // Back returns to where you were. Changing a filter or tab on the same page stays put.
  // Positions are recorded as you scroll: by the time a route change commits, the old page is gone and scrollTop is already clamped.
  useLayoutEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const url = pathname + search;
    currentUrl.current = url;
    if (lastPath.current !== pathname) {
      el.scrollTop = navType === 'POP' ? scrollByUrl.current.get(url) ?? 0 : 0;
      lastPath.current = pathname;
    }
  }, [pathname, search, navType]);

  useEffect(() => { setMenuOpen(false); }, [pathname, search]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(v => !v);
      }
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex h-dvh w-full bg-white overflow-hidden">
      <div className="hidden lg:flex shrink-0">
        <Sidebar userEmail={userEmail} onSignOut={onSignOut} className="w-60" />
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        <MobileBar onOpenMenu={() => setMenuOpen(true)} onOpenPalette={() => setPaletteOpen(true)} />
        <main
          ref={mainRef}
          onScroll={e => { scrollByUrl.current.set(currentUrl.current, e.currentTarget.scrollTop); }}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
        >
          <TopStrip onOpenPalette={() => setPaletteOpen(true)} />
          <div className="page-gutters max-w-[1440px] mx-auto pt-5 lg:pt-8">
            {children}
          </div>
        </main>
      </div>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-stone-900/40" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[min(84vw,320px)] shadow-xl">
            <Sidebar
              userEmail={userEmail}
              onSignOut={onSignOut}
              onNavigate={() => setMenuOpen(false)}
              onClose={() => setMenuOpen(false)}
              className="w-full"
            />
          </div>
        </div>
      )}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
};
